// D-5 · ShownWork — renders a working[] WorkingEntry array exactly as PRD §3.8:
// each entry shows output label, formula (symbolic), substitution, result,
// assumption lines. Refused scenarios render a red-hatched block with the
// refusal message — NEVER numbers.
import type { WorkingEntry, Refusal } from '@/types/planData';

export interface ShownWorkProps {
  working: WorkingEntry[];
  refusals?: Refusal[];
  status?: 'ok' | 'refused';
}

/** Red hatched refusal block — stripes + rule code + refusal message only (§3.8). */
export function RefusalBlock({ refusals }: { refusals: Refusal[] }) {
  return (
    <div
      data-testid="refusal-block"
      className="rounded-lg p-4 border-2 border-red-600"
      style={{
        backgroundImage:
          'repeating-linear-gradient(45deg, rgba(220,38,38,0.15) 0 10px, rgba(254,226,226,0.6) 10px 20px)',
      }}
      role="alert"
    >
      <ul className="list-disc list-inside space-y-1">
        {refusals.map((r, i) => (
          <li key={i} data-testid="refusal-message" className="text-sm text-red-800 font-semibold">
            <span className="font-mono">{r.rule}</span>: {r.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ShownWork({ working, refusals, status }: ShownWorkProps) {
  const refused = status === 'refused';
  return (
    <div className="space-y-3" data-testid="shown-work">
      {refused && <RefusalBlock refusals={refusals ?? []} />}
      {!refused &&
        working.map((entry, i) => (
          <div key={i} data-testid="working-entry" className="bg-white rounded-lg border border-blue-200 p-3">
            <div className="font-bold text-gray-900" data-testid="working-output">
              {entry.output}
            </div>
            <div className="text-sm text-gray-700 mt-1">
              <span className="text-gray-500">Formula:</span>{' '}
              <span data-testid="working-formula" className="font-mono">
                {entry.formula}
              </span>
            </div>
            <div className="text-sm text-gray-700">
              <span className="text-gray-500">Substitution:</span>{' '}
              <span data-testid="working-substitution" className="font-mono">
                {entry.substitution}
              </span>
            </div>
            <div className="text-sm text-gray-900 font-semibold">
              <span className="text-gray-500">Result:</span>{' '}
              <span data-testid="working-result">{entry.result}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1 italic" data-testid="working-assumption">
              Assumption: {entry.assumption}
            </div>
          </div>
        ))}
    </div>
  );
}

export default ShownWork;
